import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/ApiService';
import {
  collectorRequest,
  collectorWrite,
  type CollectionEntry,
  type CollectorPreferences,
  type CollectorSummary,
  type PersonalSet,
  type SpotFields,
  type Spot,
} from '../../services/api/collector';
import {
  listSpotDrafts,
  saveSpotDraft,
  removeSpotDraft,
  uploadSpotDraft,
  syncSubmittedSpotDrafts,
  type SpotDraft,
} from '../../services/collectorDrafts';
import {
  hasNativeCapability,
  requestNativePhoto,
} from '../../utils/nativeShell';
import Button from '../ui/Button';
import { prepareImageFileForUpload } from '../../utils/prepareImageFileForUpload';
import PassportChrome from '../passport/PassportChrome';
import PassportPage from '../passport/PassportPage';
import { CollectorPhoto } from './CollectorPhoto';
const inputClass =
  'block w-full mt-1 rounded-lg border border-white/20 bg-zinc-950 px-3 py-3 text-white focus:outline-amber-400';
const panel = 'rounded-2xl border border-white/10 bg-zinc-950 p-5';
const linkClass = 'inline-flex min-h-11 items-center text-amber-200 underline underline-offset-4';
const today = () => {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};
const emptyFields = (): SpotFields => ({
  title: '',
  make: '',
  model: '',
  generation: '',
  event_context: '',
  note: '',
  occurred_at: today(),
});
const errorText = (error: unknown) =>
  error instanceof Error ? error.message : 'Something went wrong. Try again.';
export function CollectorRoute({
  page,
}: {
  page: 'cars' | 'spot' | 'entry' | 'sets' | 'preferences';
}) {
  const { user, isLoading } = useAuth();
  const location = useLocation();
  if (isLoading) return <p role="status">Loading Passport…</p>;
  if (!user)
    return <Navigate to="/login" state={{ from: location.pathname + location.search }} replace />;
  return <CollectorContent key={`${user.id}:${page}`} page={page} userId={Number(user.id)} />;
}
function CollectorContent({ page, userId }: { page: string; userId: number }) {
  return (
    <PassportChrome
      active={page === 'sets' ? 'collections' : page === 'preferences' ? 'settings' : 'cars'}
    >
      {page === 'cars' ? (
        <Cars userId={userId} />
      ) : page === 'spot' ? (
        <SpotEditor userId={userId} />
      ) : page === 'entry' ? (
        <Entry />
      ) : page === 'sets' ? (
        <Sets />
      ) : (
        <Preferences />
      )}
    </PassportChrome>
  );
}
function CaptureLinks() {
  return <Link className={linkClass} to="/passport">Open your QR collection</Link>;
}
function Cars({ userId }: { userId: number }) {
  const [params, setParams] = useSearchParams();
  const [list, setList] = useState<{
    items: CollectionEntry[];
    total: number;
    page: number;
    pages: number;
  } | null>(null);
  const [summary, setSummary] = useState<CollectorSummary | null>(null);
  const [disabled, setDisabled] = useState(false);
  const [drafts, setDrafts] = useState<SpotDraft[]>([]);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    void api.trackEvent('collector_hub_viewed');
  }, []);
  const query = params.toString();
  const scrollKey = `collector-scroll:${userId}:${query}`;
  useEffect(() => {
    const remember = () => {
      try {
        sessionStorage.setItem(scrollKey, String(window.scrollY));
      } catch {
        /* Optional UI preference. */
      }
    };
    window.addEventListener('scroll', remember, { passive: true });
    return () => window.removeEventListener('scroll', remember);
  }, [scrollKey]);
  useEffect(() => {
    if (!list) return;
    try {
      const y = Number(sessionStorage.getItem(scrollKey) || 0);
      if (y > 0) requestAnimationFrame(() => window.scrollTo(0, y));
    } catch {
      /* Optional UI preference. */
    }
  }, [list, scrollKey]);
  useEffect(() => {
    let current = true;
    const sync = () => {
      if (navigator.onLine)
        void syncSubmittedSpotDrafts(userId)
          .then((saved) => {
            if (current && saved) setRetry((r) => r + 1);
          })
          .catch(() => undefined);
    };
    sync();
    window.addEventListener('online', sync);
    return () => {
      current = false;
      window.removeEventListener('online', sync);
    };
  }, [userId]);
  useEffect(() => {
    let current = true;
    setError('');
    setList(null);
    void Promise.all([
      collectorRequest<typeof list>('list', {}, Object.fromEntries(new URLSearchParams(query))),
      collectorRequest<CollectorSummary>('summary'),
      listSpotDrafts(userId),
    ])
      .then(([items, totals, saved]) => {
        if (current) {
          setList(items);
          setSummary(totals);
          setDrafts(saved);
        }
      })
      .catch((e) => {
        if (current) {
          if (e?.code === 'collector_disabled') setDisabled(true);
          else setError(errorText(e));
        }
      });
    return () => {
      current = false;
    };
  }, [query, retry, userId]);
  const filter = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    next.set(key, value);
    next.delete('page');
    setParams(next);
  };
  if (disabled) return <PassportPage />;
  return (
    <div className="space-y-6">
      <header>
        <p className="text-sm text-amber-200">Your private collection</p>
        <h1 className="mt-2 text-3xl font-semibold">Cars worth remembering.</h1>
        <p className="mt-3 text-zinc-400 max-w-2xl">
          Keep cars you spot, QR encounters, and the stories that go with them. You don’t need to
          own a car to start.
        </p>
        <CaptureLinks />
      </header>
      {summary && (
        <p className="text-zinc-300">
          {summary.entries} entries · {summary.linked_cars} linked Bilt cars ·{' '}
          {summary.identified_models} identified models · {summary.memories} memories ·{' '}
          {summary.pending} encounters awaiting verification
        </p>
      )}
      {drafts.length > 0 && (
        <section className={panel}>
          <h2 className="font-semibold">{drafts.length} drafts on this device</h2>
          <p className="text-sm text-zinc-400">
            Not yet saved to your account. Submitted drafts retry when you open Passport online.
            Reopen any draft to review it. Clearing this app’s storage removes local drafts.
          </p>
          {drafts.map((d) => (
            <Link
              key={d.id}
              className={`${linkClass} mr-5`}
              to={`/passport/spots/new?draft=${d.id}`}
            >
              {d.title || 'Unidentified car'} —{' '}
              {d.readyToSync ? 'waiting to upload' : 'reopen draft'}
            </Link>
          ))}
        </section>
      )}
      <form
        className="grid gap-3 sm:grid-cols-5"
        onSubmit={(e) => {
          e.preventDefault();
          const form = new FormData(e.currentTarget);
          filter('q', String(form.get('q') || ''));
        }}
      >
        <label>
          Search
          <input
            name="q"
            defaultValue={params.get('q') || ''}
            placeholder="Make, model, or title"
            className={inputClass}
            maxLength={100}
          />
        </label>
        <label>
          Source
          <select
            className={inputClass}
            value={params.get('source') || ''}
            onChange={(e) => filter('source', e.target.value)}
          >
            <option value="">All sources</option>
            <option value="photo">Photo spots</option>
            <option value="qr">QR encounters</option>
          </select>
        </label>
        <label>
          Year
          <input
            className={inputClass}
            type="number"
            min={1886}
            max={2100}
            placeholder="All years"
            value={params.get('year') || ''}
            onChange={(e) => filter('year', e.target.value)}
          />
        </label>
        <label>
          Order
          <select
            className={inputClass}
            value={params.get('sort') || 'newest'}
            onChange={(e) => filter('sort', e.target.value)}
          >
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
          </select>
        </label>
        <div className="self-end">
          <Button type="submit" variant="secondary">
            Search collection
          </Button>
        </div>
      </form>
      {error && (
        <div role="alert">
          {error} <Button onClick={() => setRetry((r) => r + 1)}>Retry</Button>
        </div>
      )}
      {!list && !error && <p role="status">Loading your cars…</p>}
      {list?.total === 0 && (
        <section className={panel}>
          <h2 className="text-xl">
            {query ? 'No matching cars' : 'Your first discovery starts here'}
          </h2>
          <p className="mt-2 text-zinc-400">
            {query
              ? 'Try another search or source filter.'
              : 'Take a photo or choose one from your library. Add the make and model now, or identify it later.'}
          </p>
          <CaptureLinks />
        </section>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {list?.items.map((entry) => (
          <Link
            key={entry.key}
            to={`/passport/car?entry=${encodeURIComponent(entry.key)}`}
            className={`${panel} hover:border-amber-300/50 focus-visible:outline-amber-300`}
          >
            {entry.image_spot_id ? (
              <CollectorPhoto id={entry.image_spot_id} alt={entry.title} />
            ) : (
              <div className="aspect-[4/3] bg-zinc-900 rounded-xl flex items-center justify-center text-zinc-400">
                QR encounter
              </div>
            )}
            <p className="mt-4 text-xs text-amber-200">{entry.evidence}</p>
            <h2 className="mt-1 text-lg font-semibold">{entry.title}</h2>
            <p className="text-sm text-zinc-400">
              {entry.occurred_at?.slice(0, 10)} · {entry.spots.length + entry.encounters.length}{' '}
              records
            </p>
          </Link>
        ))}
      </div>
      {list && list.pages > 1 && (
        <nav aria-label="Collection pages" className="flex items-center gap-4">
          <Button
            variant="secondary"
            disabled={list.page <= 1}
            onClick={() => {
              params.set('page', String(list.page - 1));
              setParams(params);
            }}
          >
            Previous
          </Button>
          <span>
            Page {list.page} of {list.pages}
          </span>
          <Button
            variant="secondary"
            disabled={list.page >= list.pages}
            onClick={() => {
              params.set('page', String(list.page + 1));
              setParams(params);
            }}
          >
            Next
          </Button>
        </nav>
      )}
      <p className="text-sm text-zinc-500">
        Photo-only entries are personal identifications. Linking photos to the same Bilt car groups
        them into one entry. Favorites are builds you save online; Garage contains cars you own or
        manage.
      </p>
      <Link className={linkClass} to="/passport/preferences">
        Interests, Home, and export
      </Link>
    </div>
  );
}
function Fields({ value, change }: { value: SpotFields; change: (fields: SpotFields) => void }) {
  return (
    <div className="grid sm:grid-cols-2 gap-4">
      {(
        ['title', 'make', 'model', 'generation', 'event_context', 'occurred_at', 'note'] as const
      ).map((key) => (
        <label key={key} className={key === 'note' ? 'sm:col-span-2' : ''}>
          {
            {
              title: 'Title (optional)',
              make: 'Make (optional)',
              model: 'Model (optional)',
              generation: 'Generation or chassis (optional)',
              event_context: 'Event or occasion (private, optional)',
              occurred_at: 'Spotted on',
              note: 'Your memory (optional)',
            }[key]
          }
          {key === 'note' ? (
            <textarea
              className={inputClass}
              maxLength={4000}
              rows={4}
              value={value[key]}
              onChange={(e) => change({ ...value, [key]: e.target.value })}
            />
          ) : (
            <input
              className={inputClass}
              type={key === 'occurred_at' ? 'date' : 'text'}
              required={key === 'occurred_at'}
              maxLength={
                key === 'title' || key === 'event_context' ? 160 : key === 'model' ? 100 : 80
              }
              max={key === 'occurred_at' ? today() : undefined}
              value={value[key].slice(0, key === 'occurred_at' ? 10 : undefined)}
              onChange={(e) => change({ ...value, [key]: e.target.value })}
            />
          )}
        </label>
      ))}
    </div>
  );
}
function SpotEditor({ userId }: { userId: number }) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [fields, setFields] = useState<SpotFields>(emptyFields);
  const [draft, setDraft] = useState<SpotDraft | null>(null);
  const [preview, setPreview] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(!params.get('draft'));
  const generation = useRef(api.sessionGeneration);
  const saves = useRef(Promise.resolve());
  const durable = useRef(false);
  const saveRevision = useRef(0);
  const active = () =>
    generation.current === api.sessionGeneration && Number(api.authTokens?.user?.id) === userId;
  useEffect(() => {
    let current = true;
    if (params.get('draft'))
      void listSpotDrafts(userId)
        .then((rows) => {
          if (!current) return;
          const saved = rows.find((d) => d.id === params.get('draft'));
          if (saved) {
            setDraft(saved);
            setFields(saved);
            setStatus('Draft kept on this device');
          } else setError('This draft is no longer on this device.');
          setLoaded(true);
        })
        .catch((e) => {
          if (current) {
            setError(errorText(e));
            setLoaded(true);
          }
        });
    return () => {
      current = false;
    };
  }, [params, userId]);
  const draftPhoto = draft?.photo;
  useEffect(() => {
    if (!draftPhoto) return;
    const url = URL.createObjectURL(draftPhoto);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [draftPhoto]);
  async function keep(next: SpotDraft) {
    const revision = ++saveRevision.current;
    durable.current = false;
    setStatus('Keeping draft…');
    const pending = saves.current.catch(() => undefined).then(() => saveSpotDraft(next));
    saves.current = pending;
    await pending;
    if (revision === saveRevision.current) {
      durable.current = true;
      if (active()) setStatus('Draft kept on this device');
    }
  }
  async function select(photo: Blob, source: 'camera' | 'library') {
    if (!active()) return;
    if (!photo.type.startsWith('image/') || photo.size > 20 * 1024 * 1024) {
      setError('Choose an image smaller than 20 MB.');
      return;
    }
    try {
      photo = await prepareImageFileForUpload(new File([photo], 'spot.jpg', { type: photo.type }));
    } catch {
      setError('This image could not be opened. Choose a JPEG or use the native camera.');
      return;
    }
    if (!active()) return;
    if (photo.size > 5 * 1024 * 1024) {
      setError('This photo is too large. Choose a smaller image.');
      return;
    }
    const next: SpotDraft = {
      ...fields,
      id: draft?.id || crypto.randomUUID(),
      userId,
      photo,
      capture_source: source,
      group_entry_key: draft ? draft.group_entry_key : params.get('group') || undefined,
      updatedAt: Date.now(),
    };
    setDraft(next);
    setError('');
    try {
      await keep(next);
    } catch (e) {
      setError(errorText(e));
      setStatus('Draft not saved');
    }
  }
  async function native(source: 'camera' | 'library') {
    setBusy(true);
    setError('');
    try {
      const result = await requestNativePhoto({ purpose: 'passport_spot', source });
      if (!active()) return;
      if (!result.ok || !result.uri?.startsWith('data:image/jpeg;base64,')) {
        if (result.error !== 'cancelled')
          throw new Error(result.error || 'Could not load this photo. Try the file picker.');
        return;
      }
      const blob = await (await fetch(result.uri)).blob();
      await select(blob, source);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }
  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!draft || !active()) return;
    setBusy(true);
    setError('');
    void api.trackEvent('collector_photo_save_started');
    try {
      const next = { ...draft, ...fields, updatedAt: Date.now(), readyToSync: true };
      await keep(next);
      const spot = await uploadSpotDraft(next);
      void api.trackEvent('collector_photo_save_succeeded');
      if (active())
        navigate(`/passport/car?entry=${encodeURIComponent(spot.entry_key || `spot:${spot.id}`)}`, {
          replace: true,
        });
    } catch (e) {
      if (active()) {
        setError(errorText(e));
        setStatus(
          durable.current
            ? 'Draft kept. Retry when you’re ready.'
            : 'Draft could not be kept. Keep this screen open and retry.'
        );
        void api.trackEvent('collector_photo_save_failed');
      }
    } finally {
      setBusy(false);
    }
  }
  if (!loaded) return <p role="status">Opening draft…</p>;
  return (
    <form onSubmit={submit} className="space-y-6 max-w-2xl">
      <h1 className="text-3xl font-semibold">Photo spot</h1>
      <p className="text-zinc-400">
        Your photo and notes stay private. No location is required. Avoid including faces, plates,
        or personal details you don’t want to keep.
      </p>
      {hasNativeCapability('native_photo_spots') && (
        <div className="flex gap-3">
          <Button disabled={busy} onClick={() => void native('camera')}>
            Take photo
          </Button>
          <Button variant="secondary" disabled={busy} onClick={() => void native('library')}>
            Photo library
          </Button>
        </div>
      )}
      <label className="block">
        Choose a photo
        <input
          disabled={busy}
          className={`${inputClass} file:mr-4`}
          type="file"
          accept="image/*"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void select(file, 'library');
          }}
        />
      </label>
      {preview && (
        <img
          src={preview}
          alt="Your car spot preview"
          className="w-full max-h-96 object-contain rounded-xl bg-zinc-900"
        />
      )}
      <Fields
        value={fields}
        change={(next) => {
          setFields(next);
          if (draft) {
            const changed = { ...draft, ...next, updatedAt: Date.now() };
            setDraft(changed);
            void keep(changed).catch((e) => {
              setError(errorText(e));
              setStatus('Changes not kept');
            });
          }
        }}
      />
      {draft?.group_entry_key && (
        <Button
          variant="secondary"
          onClick={() => {
            const next = { ...draft, group_entry_key: undefined };
            setDraft(next);
            void keep(next).catch((e) => setError(errorText(e)));
          }}
        >
          Save as a separate car instead
        </Button>
      )}
      <p role="status" className="text-sm text-zinc-400">
        {status}
      </p>
      {error && (
        <p role="alert" className="text-red-300">
          {error}
        </p>
      )}
      <div className="flex flex-wrap gap-4">
        <Button type="submit" disabled={!draft} loading={busy} loadingLabel="Saving…">
          Save to Passport
        </Button>
        <Link className={linkClass} to="/passport">
          Back to collection
        </Link>
        {draft && (
          <Button
            variant="danger"
            disabled={busy}
            onClick={() => {
              void saves.current
                .catch(() => undefined)
                .then(() => removeSpotDraft(draft.id, userId))
                .then(() => navigate('/passport'))
                .catch((e) => setError(errorText(e)));
            }}
          >
            Discard draft
          </Button>
        )}
      </div>
      <p className="text-sm text-zinc-500">
        Drafts are kept on this device for this account. “Saved to Passport” means the server has
        your photo. Clearing device storage can remove unsaved drafts.
      </p>
    </form>
  );
}
function Entry() {
  const [params] = useSearchParams();
  const key = params.get('entry') || '';
  const navigate = useNavigate();
  const [entry, setEntry] = useState<CollectionEntry | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [memory, setMemory] = useState('');
  const [memoryDate, setMemoryDate] = useState(today());
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const memoryId = useRef(crypto.randomUUID());
  const load = useCallback(async () => {
    setEntry(await collectorRequest<CollectionEntry>('entry', {}, { id: key }));
  }, [key]);
  useEffect(() => {
    let current = true;
    setEntry(null);
    void collectorRequest<CollectionEntry>('entry', {}, { id: key })
      .then((value) => {
        if (current) setEntry(value);
      })
      .catch((e) => {
        if (current) setError(errorText(e));
      });
    return () => {
      current = false;
    };
  }, [key]);
  async function action(fn: () => Promise<unknown>) {
    setBusy(true);
    setError('');
    try {
      await fn();
      return true;
    } catch (e) {
      setError(errorText(e));
      return false;
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="space-y-6 max-w-3xl">
      {error && (
        <p role="alert">
          {error}{' '}
          <Button variant="secondary" onClick={() => void action(load)}>
            Retry
          </Button>
        </p>
      )}
      {!entry ? (
        <p role="status">{error ? 'Entry unavailable' : 'Opening entry…'}</p>
      ) : (
        <>
          <header>
            <p className="text-sm text-amber-200">Private · {entry.evidence}</p>
            <h1 className="text-3xl font-semibold mt-2">{entry.title}</h1>
            <Link
              className={linkClass}
              to={`/passport/spots/new?group=${encodeURIComponent(entry.key)}`}
            >
              Add another photo spot
            </Link>
            {entry.vehicle && (
              <Link className={linkClass} to={entry.vehicle.path}>
                Visit the public build
              </Link>
            )}
            <p className="text-sm text-zinc-400 mt-3">
              A photo spot records your own memory. A static QR scan records the code; it does not
              prove physical presence.
            </p>
          </header>
          {entry.spots.map((spot) => (
            <section key={spot.id} className={`${panel} space-y-4`}>
              <CollectorPhoto id={spot.id} alt={spot.title} />
              <h2 className="font-semibold">Photo spot · {spot.occurred_at.slice(0, 10)}</h2>
              <SpotEditForm
                spot={spot}
                busy={busy}
                save={(fields) =>
                  action(async () => {
                    await collectorWrite('spot', fields, 'PATCH', spot.id);
                    await load();
                  })
                }
              />
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  const form = new FormData(e.currentTarget);
                  void action(async () => {
                    const updated = await collectorWrite<Spot>(
                      'spot',
                      { vehicle_url: String(form.get('vehicle_url') || '') },
                      'PATCH',
                      spot.id
                    );
                    navigate(`/passport/car?entry=${encodeURIComponent(updated.entry_key)}`, {
                      replace: true,
                    });
                  });
                }}
                className="space-y-3"
              >
                <label>
                  Link to the same car on Bilt
                  <input
                    name="vehicle_url"
                    placeholder="Paste its public Bilt build link"
                    className={inputClass}
                    maxLength={500}
                  />
                </label>
                <p className="text-sm text-zinc-400">
                  Only link if this photo shows that car. It groups your records and doesn’t verify
                  the photo or grant ownership. Leave blank to undo the link.
                </p>
                <Button type="submit" disabled={busy} variant="secondary">
                  {spot.linked_vehicle_id ? 'Update or undo link' : 'Link car'}
                </Button>
              </form>
              {confirmDelete === spot.id ? (
                <div role="alert">
                  <p>Delete this photo and its personal entry notes? This cannot be undone.</p>
                  <Button
                    variant="danger"
                    disabled={busy}
                    onClick={() =>
                      void action(async () => {
                        await collectorWrite('spot', {}, 'DELETE', spot.id);
                        navigate('/passport');
                      })
                    }
                  >
                    Delete permanently
                  </Button>
                  <Button variant="ghost" onClick={() => setConfirmDelete(null)}>
                    Keep photo
                  </Button>
                </div>
              ) : (
                <Button variant="danger" onClick={() => setConfirmDelete(spot.id)}>
                  Delete photo spot
                </Button>
              )}
            </section>
          ))}
          {entry.encounters.map((encounter) => (
            <Link
              className={`${panel} block`}
              key={encounter.public_id}
              to={`/passport/encounters/${encounter.public_id}`}
            >
              <span className="text-amber-200">{encounter.evidence}</span>
              <p>{encounter.occurred_at.slice(0, 10)} · View encounter</p>
            </Link>
          ))}
          <section className={`${panel} space-y-4`}>
            <h2 className="text-xl font-semibold">Memories</h2>
            <p className="text-sm text-zinc-400">
              Another sighting or a detail worth keeping. Memories don’t add another car to your
              count.
            </p>
            {entry.memories.map((item) => (
              <div key={item.id} className="border-b border-white/10 pb-3">
                <p className="text-sm text-zinc-500">{item.occurred_at.slice(0, 10)}</p>
                <p className="whitespace-pre-wrap">{item.note}</p>
                <Button
                  variant="ghost"
                  disabled={busy}
                  onClick={() =>
                    void action(async () => {
                      await collectorWrite('memory', {}, 'DELETE', String(item.id));
                      await load();
                    })
                  }
                >
                  Remove memory
                </Button>
              </div>
            ))}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void action(async () => {
                  await collectorWrite('memory', {
                    entry_key: key,
                    client_id: memoryId.current,
                    note: memory,
                    occurred_at: memoryDate,
                  });
                  memoryId.current = crypto.randomUUID();
                  setMemory('');
                  await load();
                });
              }}
              className="space-y-3"
            >
              <label>
                Memory
                <textarea
                  className={inputClass}
                  maxLength={4000}
                  value={memory}
                  onChange={(e) => setMemory(e.target.value)}
                  required
                />
              </label>
              <label>
                Date
                <input
                  type="date"
                  className={inputClass}
                  value={memoryDate}
                  onChange={(e) => setMemoryDate(e.target.value)}
                  required
                />
              </label>
              <Button type="submit" disabled={busy || !memory.trim()}>
                Save memory
              </Button>
            </form>
          </section>
        </>
      )}
    </div>
  );
}
function SpotEditForm({
  spot,
  busy,
  save,
}: {
  spot: Spot;
  busy: boolean;
  save: (fields: SpotFields) => Promise<boolean>;
}) {
  const [fields, setFields] = useState<SpotFields>(spot);
  const [saved, setSaved] = useState(false);
  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        void save(fields).then(setSaved);
      }}
    >
      <Fields
        value={fields}
        change={(next) => {
          setFields(next);
          setSaved(false);
        }}
      />
      <Button type="submit" variant="secondary" disabled={busy}>
        Save details
      </Button>
      {saved && <p role="status">Details saved</p>}
    </form>
  );
}
function Sets() {
  useEffect(() => {
    void api.trackEvent('collector_set_viewed');
  }, []);
  const [sets, setSets] = useState<PersonalSet[] | null>(null);
  const [error, setError] = useState('');
  const [params] = useSearchParams();
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let current = true;
    void collectorRequest<PersonalSet[]>('sets')
      .then((value) => {
        if (current) setSets(value);
      })
      .catch((e) => {
        if (current) setError(errorText(e));
      });
    return () => {
      current = false;
    };
  }, [retry]);
  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-3xl font-semibold">Sets</h1>
        <p className="mt-3 text-zinc-400">
          Small reasons to keep looking. Personal sets accept photo spots; official event
          collections keep their own verification rules.
        </p>
      </header>
      {error && (
        <p role="alert">
          {error} <Button onClick={() => setRetry((r) => r + 1)}>Retry</Button>
        </p>
      )}
      {!sets && !error && <p role="status">Loading your progress…</p>}
      <div className="grid md:grid-cols-2 gap-5">
        {sets
          ?.filter((set) => !params.get('set') || set.slug === params.get('set'))
          .map((set) => {
            const done = set.slots.filter((slot) => slot.done).length;
            return (
              <section key={set.slug} className={`${panel} space-y-4`}>
                <h2 className="text-xl font-semibold">
                  <Link to={`/passport/sets?set=${set.slug}`}>{set.title}</Link>
                </h2>
                <p className="text-zinc-400">{set.description}</p>
                <p className="text-sm">
                  {done} of {set.slots.length} ·{' '}
                  {done === set.slots.length ? 'Complete' : 'In progress'}
                </p>
                <progress
                  aria-label={`${set.title} progress`}
                  className="w-full accent-amber-300"
                  max={set.slots.length}
                  value={done}
                />
                <ul className="space-y-2">
                  {set.slots.map((slot) => (
                    <li key={slot.label} className="flex justify-between">
                      <span>{slot.label}</span>
                      <span className={slot.done ? 'text-amber-200' : 'text-zinc-500'}>
                        {slot.done ? 'Collected' : 'Still to find'}
                      </span>
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-zinc-500">
                  Accepts {set.evidence.toLowerCase()} · Version {set.version}. Progress updates
                  when records change or are removed.
                </p>
                <CaptureLinks />
              </section>
            );
          })}
      </div>
      <Link className={linkClass} to="/passport/collections">
        Official encounter sets
      </Link>
      <div className={`${panel} flex flex-wrap gap-6`}>
        <Link className={linkClass} to="/events">
          Find an event
        </Link>
        <Link className={linkClass} to="/clubs">
          Explore clubs
        </Link>
        <Link className={linkClass} to="/shops">
          Explore shops
        </Link>
      </div>
    </div>
  );
}
export function Preferences() {
  const [prefs, setPrefs] = useState<CollectorPreferences | null>(null);
  const [status, setStatus] = useState('');
  useEffect(() => {
    let current = true;
    void collectorRequest<CollectorPreferences>('settings')
      .then((p) => {
        if (current) setPrefs(p);
      })
      .catch((e) => {
        if (current) setStatus(errorText(e));
      });
    return () => {
      current = false;
    };
  }, []);
  async function save(next: CollectorPreferences) {
    try {
      const result = await collectorWrite<CollectorPreferences>('settings', next);
      setPrefs(result);
      setStatus('Preferences saved');
      void api.trackEvent('collector_preferences_saved', {
        metadata: { interests: next.interests, home_focus: next.home_focus },
      });
      window.dispatchEvent(new Event('collector-preferences-changed'));
    } catch (e) {
      setStatus(errorText(e));
    }
  }
  return (
    <section className="space-y-6 max-w-2xl">
      <h1 className="text-3xl font-semibold">Make Bilt yours</h1>
      <p className="text-zinc-400">
        Collect cars, build your own, enjoy events—or all three. These preferences change your Home,
        not your account permissions.
      </p>
      {prefs && (
        <>
          <fieldset className={`${panel} space-y-3`}>
            <legend className="px-2">I’m here to…</legend>
            {(['collect', 'build', 'events'] as const).map((interest) => (
              <label className="flex items-center gap-3 min-h-11" key={interest}>
                <input
                  type="checkbox"
                  className="size-5 accent-amber-300"
                  checked={prefs.interests.includes(interest)}
                  onChange={(e) =>
                    void save({
                      ...prefs,
                      interests: e.target.checked
                        ? [...prefs.interests, interest]
                        : prefs.interests.filter((i) => i !== interest),
                    })
                  }
                />
                {
                  {
                    collect: 'Spot and collect cars',
                    build: 'Document my builds',
                    events: 'Find events and communities',
                  }[interest]
                }
              </label>
            ))}
          </fieldset>
          <label className="block">
            On Home, show me
            <select
              className={inputClass}
              value={prefs.home_focus}
              onChange={(e) =>
                void save({
                  ...prefs,
                  home_focus: e.target.value as CollectorPreferences['home_focus'],
                })
              }
            >
              <option value="auto">A mix based on what I do</option>
              <option value="collect">My collection first</option>
              <option value="build">My builds first</option>
              <option value="both">Collection and builds</option>
            </select>
          </label>
        </>
      )}
      <p role="status">{status}</p>
      <Link className={linkClass} to="/passport/settings">
        Passport visibility settings
      </Link>
      <p className="text-sm text-zinc-400">
        Photo spots and personal memories are private, including when your Passport profile is
        public. Photos have location metadata removed before server storage. This export contains
        your notes and identifications.
      </p>
      <Button
        variant="secondary"
        onClick={() => {
          void collectorRequest<unknown>('export')
            .then((data) => {
              const url = URL.createObjectURL(
                new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
              );
              const a = document.createElement('a');
              a.href = url;
              a.download = 'bilt-private-collection.json';
              a.click();
              setTimeout(() => URL.revokeObjectURL(url), 1000);
            })
            .catch((e) => setStatus(errorText(e)));
        }}
      >
        Export my collection
      </Button>
    </section>
  );
}
